import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, PencilSimple } from '@phosphor-icons/react';
import PrimaryButton from '../../../../components/atoms/Button';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Drawer,
	DrawerContent,
	DrawerTitle,
} from '../../../../components/ui/drawer';
import { type ParsedFullName } from '../../../../shared/utils/parseFullName.util';
import { useRfcGenerator } from '../../../../features/onboarding/hooks/useRfcGenerator';
import AsatPromoDrawer from './AsatPromoDrawer';
import {
	useLinkingTracking,
	type LinkingAction,
	type RfcHelpEntryPoint,
} from '../orchestration/useLinkingTracking';

const CURP_LENGTH = 18;
const NAME_PLACEHOLDER_EXAMPLE = 'Ej. María Fernanda López García';

type DrawerView = 'gate' | 'form' | 'result';

type RfcHelpDrawerProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Quién lo abrió; se emite como `entry_point` en rfc_drawer_opened para
	 * separar el «?» del input de la fila del modal de ayuda. */
	entryPoint?: RfcHelpEntryPoint;
	initialCurp?: string;
	initialFullName?: string;
	onRfcGenerated: (result: {
		rfc: string;
		curp: string;
		name: ParsedFullName;
	}) => void;
};

const RfcHelpDrawer = ({
	open,
	onOpenChange,
	entryPoint,
	initialCurp = '',
	initialFullName = '',
	onRfcGenerated,
}: RfcHelpDrawerProps) => {
	const {
		fullName,
		curp,
		isLoading,
		isSearchingSat,
		isCurpLocked,
		nameError,
		curpError,
		generalError,
		generatedRfc,
		generatedSource,
		onNameChange,
		onCurpChange,
		generate,
		persist,
		clearResult,
		reset,
	} = useRfcGenerator({ initialCurp, initialFullName });

	const { trackAction } = useLinkingTracking();

	// Generar el RFC solo existe en el método de contraseña/CIEC: la e.firma se
	// tramita presencialmente y ya trae RFC.
	const trackRfc = (action: LinkingAction, extra?: Record<string, unknown>) =>
		trackAction('link', action, extra);

	const [view, setView] = useState<DrawerView>('gate');
	const [showAsatPromo, setShowAsatPromo] = useState(false);

	useEffect(() => {
		if (open) {
			setView('gate');
			reset();
		}
		// reset is stable enough for this purpose; re-running only on open/props.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initialCurp, initialFullName]);

	// Vista del drawer: aparte del reset para que un refresh de los props del
	// prefill no vuelva a contar la apertura.
	useEffect(() => {
		if (open) trackRfc('rfc_drawer_opened', { entry_point: entryPoint ?? null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// «Generar RFC» consulta primero al SAT por la CURP (dispara la extracción
	// del banner fiscal, ya sin preguntar) y solo si no contesta calcula por
	// nombre. `source` separa los dos caminos en rfc_generate_*.
	const handleGenerate = async () => {
		trackRfc('rfc_generate_submitted');
		const { result, source } = await generate();
		trackRfc(result ? 'rfc_generate_succeeded' : 'rfc_generate_failed', {
			source,
		});
		if (result) setView('result');
	};

	const handleUseRfc = async () => {
		const result = await persist();
		trackRfc('rfc_result_used', { persisted: Boolean(result) });
		if (!result) return;
		onRfcGenerated(result);
		onOpenChange(false);
	};

	const handleBackToEdit = () => {
		trackRfc('rfc_result_edit_requested');
		clearResult();
		setView('form');
	};

	const nameInputClass = nameError
		? 'h-10 border-red-500 focus-visible:ring-red-500'
		: 'h-10';
	const curpInputClass = curpError
		? 'h-10 border-red-500 focus-visible:ring-red-500'
		: 'h-10';

	return (
		<>
		<Drawer
			open={open}
			onOpenChange={onOpenChange}>
			<DrawerContent className='inset-x-auto left-1/2 w-full max-w-lg -translate-x-1/2'>
				<DrawerTitle className='sr-only'>Generar mi RFC</DrawerTitle>
				<div className='flex flex-col gap-1 px-5 pt-2 pb-3 pr-14'>
					{view === 'form' || view === 'result' ? (
						<button
							type='button'
							onClick={() => setView('gate')}
							className='text-h-primary flex items-center gap-1 text-sm font-medium w-fit'
						>
							<ArrowLeft size={16} weight='bold' />
							Volver
						</button>
					) : null}
					<h2 className='text-h-text-heading text-xl font-bold'>
						Generar mi RFC
					</h2>
				</div>

				<div className='flex flex-col gap-4 px-5 pt-2 pb-6'>
					{view === 'gate' && (
						<div className='flex flex-col gap-4'>
							<p className='text-h-text-heading text-base font-medium'>
								¿Ya fuiste al SAT a tramitar tu RFC?
							</p>
							<p className='text-h-text-medium text-sm'>
								Cuéntanos para guiarte con los siguientes pasos.
							</p>
							<div className='flex flex-col gap-2 mt-2'>
								<PrimaryButton
									label='Sí, solo no recuerdo mi RFC'
									onClick={() => {
										trackRfc('rfc_gate_has_rfc');
										setView('form');
									}}
								/>
								<PrimaryButton
									variant='outline'
									label='No, nunca he tramitado mi RFC'
									onClick={() => {
										trackRfc('rfc_gate_never_filed');
										onOpenChange(false);
										setShowAsatPromo(true);
									}}
								/>
							</div>
						</div>
					)}

					{view === 'form' && (
						<>
							<p className='text-h-text-medium text-sm'>
								Ingresa tu nombre completo y tu CURP para generar tu RFC.
							</p>

							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Nombre completo</Label>
								<ShadcnInput
									value={fullName}
									onChange={(e) => onNameChange(e.target.value)}
									placeholder={NAME_PLACEHOLDER_EXAMPLE}
									disabled={isLoading || isCurpLocked}
									className={nameInputClass}
								/>
								{nameError ? (
									<p className='text-red-500 text-xs'>{nameError}</p>
								) : (
									<p className='text-h-text-light text-xs'>
										Ejemplo: María Fernanda López García
									</p>
								)}
							</div>

							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>CURP</Label>
								<ShadcnInput
									value={curp}
									onChange={(e) => onCurpChange(e.target.value)}
									placeholder='Ingresa tu CURP'
									maxLength={CURP_LENGTH}
									disabled={isLoading || isCurpLocked}
									className={curpInputClass}
								/>
								{curpError ? (
									<p className='text-red-500 text-xs'>{curpError}</p>
								) : (
									<p className='text-h-text-light text-xs'>
										{curp.length}/{CURP_LENGTH}
									</p>
								)}
							</div>

							{generalError && (
								<p className='text-red-500 text-sm'>{generalError}</p>
							)}

							<PrimaryButton
								label='Generar RFC'
								onClick={handleGenerate}
								loading={isLoading}
							/>
							{isSearchingSat && (
								<p className='text-h-text-light text-center text-sm'>
									Estamos buscando tu RFC en el SAT. Puede tardar unos segundos.
								</p>
							)}
						</>
					)}

					{view === 'result' && generatedRfc && (
						<div className='flex flex-col gap-4'>
							<p className='text-h-text-medium text-sm'>
								{generatedSource === 'sat_curp'
									? 'Este es tu RFC registrado en el SAT:'
									: 'Tu RFC generado es:'}
							</p>
							<div className='bg-h-primary-95 rounded-xl px-4 py-5 text-center'>
								<p className='text-h-text-heading text-2xl font-bold tracking-wider'>
									{generatedRfc}
								</p>
							</div>
							<div className='flex flex-col gap-2'>
								<PrimaryButton
									label='Usar este RFC'
									onClick={handleUseRfc}
									icon={
										<CheckCircle
											weight='fill'
											size={20}
											className='opacity-50'
										/>
									}
								/>
								<PrimaryButton
									variant='outline'
									label='Volver a editar'
									onClick={handleBackToEdit}
									icon={
										<PencilSimple
											weight='bold'
											size={18}
											className='opacity-70'
										/>
									}
								/>
							</div>
						</div>
					)}

				</div>
			</DrawerContent>
		</Drawer>

		<AsatPromoDrawer
			open={showAsatPromo}
			onOpenChange={setShowAsatPromo}
			method='link'
			entryPoint='rfc_not_filed'
		/>
		</>
	);
};

export default RfcHelpDrawer;
